<template>
  <!-- 加载失败时不再留一个点不动的空盒子：给出可见提示和重试入口 -->
  <div v-if="loadFailed" class="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-text-center">
    <p class="tw-text-sm md:tw-text-base">{{ $t('kyc.recaptcha_load_failed') }}</p>
    <UIButton class="tw-btn tw-btn-md" @click="retry">{{ $t('kyc.Refresh') }}</UIButton>
  </div>
  <div
    v-else
    :data-sitekey="siteKey || nuxtApp.$recaptcha.siteKey"
    :data-size="computedDataSize"
    :data-theme="dataTheme"
    :data-badge="dataBadge"
    :data-tabindex="dataTabindex"
    data-callback="recaptchaSuccessCallback"
    data-expired-callback="recaptchaExpiredCallback"
    data-error-callback="recaptchaErrorCallback"
    class="g-recaptcha"
  />
</template>

<script setup lang="ts">
  import { LANGUAGES } from '~/config'

  const nuxtApp = useNuxtApp()

  const props = defineProps({
    siteKey: {
      type: String,
      default: '',
    },

    dataTheme: {
      default: 'light',
      type: String,

      validator: (value: string) => {
        return ['dark', 'light'].includes(value)
      },
    },

    dataSize: {
      default: null,
      type: String,

      validator: (value: string) => {
        return ['compact', 'normal', 'invisible'].includes(value)
      },
    },

    dataBadge: {
      default: 'bottomright',
      type: String,

      validator: (value: string) => {
        return ['bottomright', 'bottomleft', 'inline'].includes(value)
      },
    },

    dataTabindex: {
      default: 0,
      type: Number,
    },
  })

  const emit = defineEmits(['error', 'success', 'expired'])

  const computedDataSize = computed(() => {
    return props.dataSize || nuxtApp.$recaptcha.size || 'normal'
  })

  const onError = function (message: string) {
    return emit('error', message)
  }

  const onSuccess = function (token: string) {
    return emit('success', token)
  }

  const onExpired = function () {
    return emit('expired')
  }

  const { locale } = useI18n()
  const currenLang = computed(() =>
    LANGUAGES[locale.value as keyof ILanguages] ? LANGUAGES[locale.value as keyof ILanguages] : LANGUAGES['en'],
  )

  const loadFailed = ref(false)

  const mount = () => {
    nuxtApp.$recaptcha.setLang(currenLang.value.recaptchaLanguageCode)
    return nuxtApp.$recaptcha.init()
  }

  const retry = async () => {
    loadFailed.value = false
    // reCAPTCHA 是自动渲染：脚本执行时会去扫 .g-recaptcha 元素。
    // 必须等 v-else 分支把容器挂回 DOM 之后再 init，否则重试会再次渲染失败。
    await nextTick()
    mount().catch(() => {
      loadFailed.value = true
    })
  }

  onMounted(async () => {
    // init() 失败（脚本被同意流程扣住、网络拦截、超时）必须让用户看见，
    // 否则页面只剩一个空白容器，看起来像「表单没加载出来」
    try {
      await mount()
    } catch (err) {
      console.error(err)
      loadFailed.value = true
    }

    nuxtApp.$recaptcha.on('recaptcha-error', onError)
    nuxtApp.$recaptcha.on('recaptcha-success', onSuccess)
    nuxtApp.$recaptcha.on('recaptcha-expired', onExpired)
  })

  onBeforeUnmount(() => {
    nuxtApp.$recaptcha.destroy()
  })

  watchEffect(() => {
    const lang = nuxtApp.$recaptcha.getLang()
    if (lang && lang !== currenLang.value.recaptchaLanguageCode) {
      setTimeout(() => {
        window.location.reload()
      }, 400)
    }
  })
</script>
